'use client';

import { useSearchParams } from 'next/navigation';
import { useEffect, useRef } from 'react';

import { useChartSettingsStore } from '@/stores/chart-settings';

/**
 * Sell deep-link preset hook (spec 083 / FR-016 / FR-018).
 *
 * Watches the URL for the portfolio Sell button's preset:
 *   `?action=sell&amount=max`
 *
 * When detected, dispatches `requestTradeDirection(market, 'sell', { amount: 'max' })`
 * via the chart-settings store. The `<TradeForm>` already consumes that store
 * via its `pendingMaxNonceRef` path — once balances + outcomes settle the form
 * flips to sell and fills with the user's max sellable amount.
 *
 * The dispatch fires once per unique URL (`action+amount` pair) per mount —
 * a `useRef` guard prevents repeat fires on every re-render. The companion
 * `?outcome=<label>` parameter is handled by the existing outcome-seed effect
 * in `MarketDetailLayout` and is not re-parsed here.
 *
 * @param marketPubkey  Active market pubkey from `MarketDetailLayout` props
 * @param outcomesLoaded  True once `useGetMarket.outcomes` has populated. The
 *                        dispatch is gated on this so `setSelectedOutcome` (in
 *                        the existing seed effect) has already populated the
 *                        outcome before the trade form's max-amount handler
 *                        consumes it.
 */
export function useSellDeeplinkPreset(marketPubkey: string, outcomesLoaded: boolean): void {
  const searchParams = useSearchParams();
  const requestTradeDirection = useChartSettingsStore((s) => s.requestTradeDirection);
  const lastFiredKey = useRef<string | null>(null);

  useEffect(() => {
    if (!outcomesLoaded) return;
    const action = searchParams.get('action');
    const amount = searchParams.get('amount');
    if (action !== 'sell' || amount !== 'max') return;

    // Compose a stable per-(market, params) key so the dispatch fires once
    // per unique URL signature. Re-renders that don't change the URL are no-ops.
    const key = `${marketPubkey}:${action}:${amount}`;
    if (lastFiredKey.current === key) return;
    lastFiredKey.current = key;

    requestTradeDirection(marketPubkey, 'sell', { amount: 'max' });
  }, [outcomesLoaded, marketPubkey, searchParams, requestTradeDirection]);
}

/**
 * Synchronous helper for telemetry: reads the current URL's search params and
 * returns the `entry_point` token to attach to a `trade_success` event when
 * the user is mid-trade on a deep-linked market. Returns `undefined` when no
 * deep-link signature is present.
 *
 * Used by `transactions.ts` at trade-emit time to close the SC-007 measurement
 * loop. Gracefully degrades to `undefined` outside browser environments.
 */
export function readSellDeeplinkEntryPoint(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const params = new URLSearchParams(window.location.search);
  if (params.get('action') === 'sell' && params.get('amount') === 'max') {
    return 'portfolio_sell_deeplink';
  }
  return undefined;
}
